import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { naytaPvm } from "./pvm";
import { useTaulukko } from "./taulukko";
import { nayttonimi, selite } from "./nayttonimi";
import { luku } from "./luvut";
import {
  api, ApiError, tanaan, maanantai, pvmTeksti, lisaaPaivia,
  type Kayttaja, type Patevyystyyppi, type PatevyysRivi,
  type PatevyysRiskiRivi, type Resurssi,
} from "./api";
import Liitteet from "./Liitteet";
import Jatkuvuus from "./Jatkuvuus";

import { Ohje } from "./Ohje";
import { t as tr, virheTeksti } from "./kieli";
/* ═══════════════════════════════════════════════════════════
   Pätevyydet.

   Neljän hitsaajan yrityksessä umpeutunut EN ISO 9606 on neljännes
   hitsauskapasiteetista. Kalenteri ei kerro siitä mitään: henkilö on
   ruudukossa vihreänä, mutta hän ei saa tehdä työtä.
   ═══════════════════════════════════════════════════════════ */

const RISKI_VIIKKOJA = 12;

export default function Patevyydet({ kayttaja, osoiteHaku, asetaOsoiteHaku }: { kayttaja: Kayttaja; osoiteHaku?: string; asetaOsoiteHaku?: (h: string) => void }) {
  // T488: oikeus `hitsauskoordinointi` koko yritykseen (pätevyys on henkilön, ei kohteen) — sama ehto kuin API.
  const saaMuokata = kayttaja.roolit.some((r) => ["paakayttaja", "suunnittelija"].includes(r))
    || kayttaja.oikeudet.includes("hitsauskoordinointi");

  const [rivit, setRivit] = useState<PatevyysRivi[]>([]);
  const [tyypit, setTyypit] = useState<Patevyystyyppi[]>([]);
  const [henkilot, setHenkilot] = useState<Resurssi[]>([]);
  const [riskit, setRiskit] = useState<PatevyysRiskiRivi[]>([]);
  const [ladataan, setLadataan] = useState(true);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [lisataan, setLisataan] = useState(false);
  const [suodin, setSuodin] = useState<"huomio" | "kaikki">("huomio");
  const [avattu, setAvattu] = useState<number | null>(null);

  const lataa = useCallback(async () => {
    setLadataan(true);
    setVirhe(null);
    const alku = pvmTeksti(maanantai(new Date()));
    const loppu = pvmTeksti(lisaaPaivia(new Date(alku), RISKI_VIIKKOJA * 7 - 1));
    try {
      const [p, t, h, r] = await Promise.all([
        api.patevyydet(), api.patevyystyypit(), api.resurssit(),
        api.patevyysriski(alku, loppu),
      ]);
      setRivit(p); setTyypit(t); setHenkilot(h.filter((x) => !x.nimeamaton)); setRiskit(r);
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("hen.haku_virhe"));
    } finally { setLadataan(false); }
  }, []);

  useEffect(() => { void lataa(); }, [lataa]);

  const huomiota = useMemo(
    () => rivit.filter((r) => r.tila !== "voimassa" && r.tila !== "ei vanhene"),
    [rivit]
  );
  const suodatetut = suodin === "huomio" ? huomiota : rivit;
  // A3–A7: jaettu lajittelu ja haku (taulukko.tsx). Oletus: voimassaolo, lähin ensin.
  const t = useTaulukko(suodatetut, {
    avaimet: { henkilo: (r) => r.nimi, patevyys: (r) => r.patevyys, myonnetty: (r) => r.myonnetty, voimassa: (r) => r.voimassa_asti, tila: (r) => r.tila },
    hakukentat: (r) => [r.nimi, r.lyhenne, r.patevyys, r.tila],
    oletus: "voimassa", tyhjaEnsin: ["voimassa", "myonnetty"], yksikko: tr("pat.yksikko"), osoiteHaku, asetaOsoiteHaku,
  });
  const nakyvat = t.rivit;

  /** Henkilöt, joilla ei ole yhtään kirjattua pätevyyttä. */
  const ilmanPatevyytta = useMemo(() => {
    const on = new Set(rivit.map((r) => r.henkilo_id));
    return henkilot.filter((h) => !on.has(h.id));
  }, [rivit, henkilot]);

  if (virhe) {
    return (
      <div className="tyhja">
        <p>{virhe}</p>
        <button className="nappi nappi--paa" onClick={() => void lataa()}>{tr("hen.yrita_uudelleen")}</button>
      </div>
    );
  }

  return (
    <>
      <header className="head">
        <div className="eyebrow">{tr("pat.eyebrow")}</div>
        <div className="head__rivi">
          <h1>{tr("pat.otsikko")}</h1>
          {saaMuokata && !lisataan && (
            <button className="nappi nappi--paa" onClick={() => setLisataan(true)}>
              {tr("pat.kirjaa")}
            </button>
          )}
        </div>
      </header>

      {/* Ohje ylös (Toni 23.9.2026): kuka saa tehdä mitä, ja mitä luvut tarkoittavat. */}
      <Ohje
        kuka={<>{tr("pat.ohje.kuka")}</>}
        luvut={<>{tr("pat.ohje.varoitus")}
          {" "}<span className="merkki--todistus">✓</span> {tr("pat.ohje.liitteena")}
          {" "}<span className="merkki--nahty">◑</span> {tr("pat.ohje.nahty")}
          {" "}<span className="merkki--epavarma">?</span> {tr("pat.ohje.ei_varmennettu")}
          {" "}{tr("pat.ohje.tarkea")}</>} />

      {/* Toni 25.9.2026: se mikä vaatii toimenpiteen tulee ennen sitä mikä on vain tiedoksi — ohje →
          toimenpidettä vaativat → viitetieto. Jatkuvuuden 37 parin taulukko täytti ruudun ja «Huomiota
          vaativat» jäi sen alle; jatkuvuus on nyt sivun lopussa. Työ umpeutuvalla pätevyydellä on
          painavin tieto tällä sivulla, ja tyhjänä lohko sanoo sen eikä katoa. */}
      {!ladataan && riskit.length === 0 && (
        <p className="dim" data-toimenpide="riskit" data-tyhja-tila="">
          {tr("pat.ei_riskeja", { n: RISKI_VIIKKOJA })}
        </p>
      )}
      {riskit.length > 0 && (
        <section className="paatokset" data-toimenpide="riskit">
          <div className="panel__head">
            <h2>{tr("pat.riskit_otsikko")}</h2>
            <span className="hint">{tr("pat.riskit_hint", { n: riskit.length, v: RISKI_VIIKKOJA })}</span>
          </div>
          <ul>
            {riskit.map((r, i) => (
              <li key={i} className="pk pk--vaje">
                <span className="pk__merkki" aria-hidden="true" />
                <div>
                  <div className="pk__otsikko">
                    {tr("pat.riski_otsikko", { nimi: r.nimi, patevyys: nayttonimi("patevyystyyppi_nimi", r.patevyys), pvm: naytaPvm(r.voimassa_asti) ?? "" })}
                  </div>
                  <div className="pk__teksti">
                    {tr("pat.riski_teksti", { kohde: r.kohde, h: luku(r.tunteja_jalkeen), pvm: naytaPvm(r.allokaatio_asti) ?? "" })}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {lisataan && (
        <UusiPatevyys
          tyypit={tyypit} henkilot={henkilot}
          peruuta={() => setLisataan(false)}
          valmis={async () => { setLisataan(false); await lataa(); }}
          paivita={lataa}
        />
      )}

      {ladataan && <div className="lataa lataa--ohut">{tr("ltar.ladataan")}</div>}

      <div className="suodin" data-toimenpide="huomio">
        <button className={"chip" + (suodin === "huomio" ? " chip--on" : "")}
                onClick={() => setSuodin("huomio")}>
          {tr("pat.huomiota_vaativat")} {huomiota.length > 0 && `(${huomiota.length})`}
        </button>
        <button className={"chip" + (suodin === "kaikki" ? " chip--on" : "")}
                onClick={() => setSuodin("kaikki")}>
          {tr("pat.kaikki", { n: rivit.length })}
        </button>
      </div>

      {!ladataan && nakyvat.length === 0 && (
        <div className="tyhja">
          <h2>{suodin === "huomio" ? tr("pat.ei_huomioitavaa") : tr("pat.ei_kirjattuja")}</h2>
          <p>
            {suodin === "huomio"
              ? tr("pat.kaikki_voimassa")
              : tr("pat.kirjaa_ensimmainen")}
          </p>
        </div>
      )}

      {nakyvat.length > 0 && (
        <section className="panel scroll">
          <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 8 }}>
            {t.Haku({ placeholder: tr("pat.haku") })}
            {t.piilotettu > 0 && <span className="dim">{tr("pat.piilossa", { n: t.piilotettu })}</span>}
          </div>
          <table className="tbl tbl--patevyydet">
            <thead>
              <tr>
                <th className="th--nimi">{t.Otsikko("henkilo", tr("tk.henkilo"))}</th>
                <th className="th--vasen">{t.Otsikko("patevyys", tr("pat.patevyys"))}</th>
                <th>{t.Otsikko("myonnetty", tr("pat.myonnetty"))}</th>
                <th>{t.Otsikko("voimassa", tr("pat.voimassa"))}</th>
                {/* «Jäljellä» + «146 vrk sitten» oli ristiriita (24.9.2026): arvo kertoo kumpaan suuntaan. */}
                <th>{tr("pat.voimassaolo")}</th>
                <th>{t.Otsikko("tila", tr("pu.tila"))}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {nakyvat.map((r) => (
                <Fragment key={r.id}>
                <tr className={"tila--" + tilaLuokka(r.tila) + (avattu === r.id ? " tr--auki" : "")}>
                  <th className="th--nimi">
                    <span className="lyh">{r.lyhenne ?? "—"}</span>
                    <span className="kokonimi">{r.nimi}</span>
                  </th>
                  <td className="td--vasen">
                    {nayttonimi("patevyystyyppi_nimi", r.patevyys)}
                    {r.taso && <span className="dim"> · {r.taso}</span>}
                    {/* Merkki johdetaan todistuksesta eikä lipusta: liite ON
                        todiste, ja lippu voi jäädä jälkeen. Kolme eri tilaa,
                        koska ne tarkoittavat eri asioita. */}
                    {r.todistuksia > 0
                      ? <span className="merkki--todistus" title={tr("pat.todistuksia_liitteena", { n: r.todistuksia })}>✓</span>
                      : r.varmennettu
                        ? <span className="merkki--nahty" title={tr("pat.nahty_ei_liitteena")}>◑</span>
                        : <span className="merkki--epavarma" title={tr("pat.ei_varmennettu_title")}>?</span>}
                  </td>
                  <td className="mono">{naytaPvm(r.myonnetty) ?? <span className="dim">—</span>}</td>
                  <td className="mono">{naytaPvm(r.voimassa_asti) ?? <span className="dim">{tr("omat.ei_vanhene")}</span>}</td>
                  <td className="mono">
                    {r.paivia_jaljella === null
                      ? <span className="dim">—</span>
                      : r.paivia_jaljella < 0
                        ? tr("pat.vrk_sitten", { n: Math.abs(r.paivia_jaljella) })
                        : tr("pat.vrk_jaljella", { n: r.paivia_jaljella })}
                  </td>
                  <td><span className={"tilamerkki tilamerkki--" + tilaLuokka(r.tila)} title={selite("patevyys_tila", r.tila)}>{nayttonimi("patevyys_tila", r.tila)}</span></td>
                  <td className="td--oikea">
                    <button className="nappi nappi--kevyt"
                            onClick={() => setAvattu(avattu === r.id ? null : r.id)}
                            aria-expanded={avattu === r.id}>
                      {avattu === r.id ? tr("hl.sulje") : tr("pat.todistus")}
                    </button>
                    {saaMuokata && (
                      <button className="nappi nappi--kevyt"
                              onClick={() => void poista(r.id)}>{tr("liite.poista")}</button>
                    )}
                  </td>
                </tr>

                {avattu === r.id && (
                  <tr className="tr--laajennus">
                    <td colSpan={7}>
                      <div className="laajennus">
                        {/* Toni 16.10.2026 klo 04.15: todistus on tavallinen työasiakirja (näkyy pätevyyden oikeudella).
                            Yksittäisen liitteen voi merkitä käsin arkaluonteiseksi, jos siinä on esim. henkilötunnus. */}
                        <Liitteet
                          kohdeTaulu="patevyys" kohdeId={r.id}
                          saaMuokata={saaMuokata}
                          otsikko={tr("pat.todistus")}
                          voiMerkitaArkaluonteiseksi
                          muuttui={lataa}
                        />
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {ilmanPatevyytta.length > 0 && suodin === "kaikki" && (
        <section className="panel">
          <div className="panel__head">
            <h2>{tr("pat.ei_kirjattuja")}</h2>
            <span className="hint">{tr("pat.henkiloa", { n: ilmanPatevyytta.length })}</span>
          </div>
          <div className="osaamiset">
            {ilmanPatevyytta.map((h) => (
              <span key={h.id} className="merkki">{h.lyhenne} · {h.nimi}</span>
            ))}
          </div>
        </section>
      )}

      {/* Jatkuvuus on lokista johdettu eikä kerro pätevyydestä mitään — siksi se on oma lohkonsa eikä
          sekoitu todistusten joukkoon. Viitetieto, joten sivun lopussa (Toni 25.9.2026). Lohko jää pois
          kokonaan jos hitsauslokia ei ole ostettu. */}
      <Jatkuvuus />
    </>
  );

  async function poista(id: number) {
    try {
      await api.poistaPatevyys(id);
      await lataa();
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("liite.poisto_virhe"));
    }
  }
}

/* ── kirjaus ────────────────────────────────────────────── */

function UusiPatevyys({
  tyypit, henkilot, peruuta, valmis, paivita,
}: {
  tyypit: Patevyystyyppi[]; henkilot: Resurssi[];
  peruuta: () => void; valmis: () => Promise<void>; paivita: () => Promise<void>;
}) {
  const [henkiloId, setHenkiloId] = useState<number | "">("");
  const [tyyppiId, setTyyppiId] = useState<number | "">("");
  const [myonnetty, setMyonnetty] = useState(tanaan());
  const [voimassa, setVoimassa] = useState("");
  // Käsin annettu päivä ei saa kadota, jos tyyppiä vaihdetaan sen
  // jälkeen — automatiikka väistää käyttäjän syötteen.
  const [kasinAnnettu, setKasinAnnettu] = useState(false);
  const [varmennettu, setVarmennettu] = useState(false);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [tallennetaan, setTallennetaan] = useState(false);
  const [uusiTyyppi, setUusiTyyppi] = useState("");
  // Todistus valitaan ennen tallennusta, mutta ladataan sen jälkeen:
  // pätevyyden id on tiedossa vasta silloin.
  const [todistus, setTodistus] = useState<File | null>(null);
  const tiedostoSyote = useRef<HTMLInputElement>(null);

  const tyyppi = tyypit.find((t) => t.id === tyyppiId);

  /** Voimassaolo tyypin kestosta. Tulityökortti 5 v, EN ISO 9606 3 v. */
  const laskettu = useMemo(() => {
    if (!tyyppi?.voimassa_kk || !myonnetty) return null;
    const d = new Date(myonnetty);
    d.setUTCMonth(d.getUTCMonth() + tyyppi.voimassa_kk);
    return pvmTeksti(d);
  }, [tyyppi, myonnetty]);

  /**
   * Laskettu päivä menee suoraan kenttään.
   *
   * Aiemmin se näkyi vain vihjetekstinä ja placeholderina — mutta
   * type="date" ei näytä placeholderia lainkaan, joten ehdotus jäi
   * näkymättömiin ja päivä piti laskea käsin. Tieto jonka järjestelmä
   * tietää ei kuulu käyttäjän laskettavaksi.
   */
  useEffect(() => {
    if (!kasinAnnettu) setVoimassa(laskettu ?? "");
  }, [laskettu, kasinAnnettu]);

  async function tallenna(e: React.FormEvent) {
    e.preventDefault();
    setVirhe(null);
    if (henkiloId === "" || tyyppiId === "") return;
    setTallennetaan(true);
    try {
      const p = await api.kirjaaPatevyys({
        henkilo_id: henkiloId, patevyystyyppi_id: tyyppiId, varmennettu,
        ...(myonnetty && { myonnetty }),
        ...(voimassa && { voimassa_asti: voimassa }),
      });

      if (todistus) {
        try {
          await api.lisaaLiite({
            kohde_taulu: "patevyys", kohde_id: p.id,
            nimi: todistus.name,
            sisalto: await lueBase64(todistus),
            arkaluonteinen: false,   // Toni 16.10.2026 klo 04.15: todistus ei ole arkaluonteinen
          });
        } catch (e) {
          // Pätevyys on tallessa vaikka liite epäonnistuisi — sitä ei
          // peruta, vaan kerrotaan mitä jäi tekemättä.
          setVirhe(
            tr("pat.liite_virhe", { virhe: e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("pat.tuntematon_virhe") })
          );
          setTallennetaan(false);
          await valmis();
          return;
        }
      }
      await valmis();
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("ltar.tallennus_virhe"));
      setTallennetaan(false);
    }
  }

  async function lisaaTyyppi() {
    if (!uusiTyyppi.trim()) return;
    setTallennetaan(true);
    setVirhe(null);
    try {
      const t = await api.luoPatevyystyyppi({ nimi: uusiTyyppi.trim() });
      setUusiTyyppi("");
      await paivita();
      setTyyppiId(t.id);
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : tr("ltar.tallennus_virhe"));
    } finally { setTallennetaan(false); }
  }

  return (
    <section className="panel lomake">
      <div className="panel__head">
        <h2>{tr("pat.kirjaa")}</h2>
        <span className="hint">{tr("pat.uusinta_korvaa")}</span>
      </div>
      <form className="lomake__kentat" onSubmit={tallenna}>
        <label className="kasvaa">
          {tr("tk.henkilo")}
          <select value={henkiloId} required
                  onChange={(e) => setHenkiloId(e.target.value ? Number(e.target.value) : "")}>
            <option value="">{tr("hl.valitse")}</option>
            {henkilot.map((h) => (
              <option key={h.id} value={h.id}>{h.lyhenne} · {h.nimi}</option>
            ))}
          </select>
        </label>
        <label className="kasvaa">
          {tr("pat.patevyys")}
          <select value={tyyppiId} required
                  onChange={(e) => {
                    setTyyppiId(e.target.value ? Number(e.target.value) : "");
                    if (!voimassa) setKasinAnnettu(false);
                  }}>
            <option value="">{tr("hl.valitse")}</option>
            {tyypit.map((t) => (
              <option key={t.id} value={t.id}>
                {t.nimi}{t.voimassa_kk ? ` (${tr("pat.kk", { kk: t.voimassa_kk })})` : ""}
              </option>
            ))}
          </select>
        </label>
        <label>
          {tr("pat.myonnetty")}
          <input type="date" value={myonnetty} onChange={(e) => setMyonnetty(e.target.value)} />
        </label>
        <label>
          {tr("pat.voimassa_asti")}
          <input type="date" value={voimassa}
                 onChange={(e) => { setVoimassa(e.target.value); setKasinAnnettu(true); }} />
        </label>

        <label className="valinta valinta--leveä">
          <input type="checkbox" checked={varmennettu} disabled={!!todistus}
                 onChange={(e) => setVarmennettu(e.target.checked)} />
          {tr("pat.nahty_todistuksesta")}
        </label>

        <div className="lomake__osaamiset">
          <span className="lomake__otsikko">{tr("pat.todistus")}</span>
          <div className="liitteet__lisays">
            <input
              ref={tiedostoSyote} type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,application/pdf"
              onChange={(e) => {
                const t = e.target.files?.[0] ?? null;
                setTodistus(t);
                // Todistus nähtynä tarkoittaa juuri tätä: kuva on siitä.
                if (t) setVarmennettu(true);
              }}
            />
            {todistus && (
              <button type="button" className="nappi nappi--kevyt"
                      onClick={() => {
                        setTodistus(null);
                        if (tiedostoSyote.current) tiedostoSyote.current.value = "";
                      }}>
                {tr("pat.poista_valinta")}
              </button>
            )}
          </div>
          {todistus && (
            <p className="ikkuna__vihje">
              {tr("pat.liite_todentaa")}
            </p>
          )}
        </div>

        <div className="lomake__toiminnot">
          <button type="submit" className="nappi nappi--paa"
                  disabled={tallennetaan || henkiloId === "" || tyyppiId === ""}>
            {tallennetaan ? tr("pu.tallennetaan") : tr("ltar.tallenna")}
          </button>
          <button type="button" className="nappi nappi--kevyt" onClick={peruuta}>{tr("hl.peruuta")}</button>
        </div>

        {tyyppi && (
          <p className="ikkuna__vihje" style={{ flex: "1 1 100%" }}>
            {kasinAnnettu && voimassa !== laskettu
              ? <>{tr("pat.kasin_annettu")}{" "}
                  <button type="button" className="tekstilinkki"
                          onClick={() => { setKasinAnnettu(false); setVoimassa(laskettu ?? ""); }}>
                    {laskettu ?? tr("omat.ei_vanhene")}
                  </button>.</>
              : tyyppi.voimassa_kk
                ? <>{tr("pat.laskettu_kestosta", { kk: tyyppi.voimassa_kk })}</>
                : <>{tr("pat.ei_vanhene_vihje")}</>}
          </p>
        )}

        {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
      </form>

      <div className="allokointi">
        <label className="kasvaa">
          {tr("pat.uusi_tyyppi")}
          <input value={uusiTyyppi} placeholder={tr("pat.uusi_tyyppi_esim")} maxLength={100}
                 onChange={(e) => setUusiTyyppi(e.target.value)} />
        </label>
        <button type="button" className="nappi nappi--kevyt"
                disabled={tallennetaan || !uusiTyyppi.trim()}
                onClick={() => void lisaaTyyppi()}>
          {tr("pat.lisaa_tyyppi")}
        </button>
      </div>
    </section>
  );
}

/* ── apurit ─────────────────────────────────────────────── */



const tilaLuokka = (tila: string) =>
  tila === "vanhentunut" ? "vanhentunut"
  : tila === "kriittinen" ? "kriittinen"
  : tila === "varoitus" ? "varoitus"
  : "ok";

/** Tiedosto base64-merkkijonoksi. data:-etuliitteen poistaa palvelin. */
const lueBase64 = (tiedosto: File): Promise<string> =>
  new Promise((valmis, virhe) => {
    const lukija = new FileReader();
    lukija.onload = () => valmis(String(lukija.result));
    lukija.onerror = () => virhe(new Error(tr("pat.tiedoston_luku")));
    lukija.readAsDataURL(tiedosto);
  });
